// src/contexts/CartContext.jsx
import React, { createContext, useState, useEffect } from 'react';
import { getStorageItem, setStorageItem } from '../utils/storage';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [currentlyDisplayed, setCurrentlyDisplayed] = useState(null);
  const [currentView, setCurrentView] = useState('selection'); // 'selection' ou 'cart' ou 'delivery'

  // Constante pour le montant minimal de commande
  const MINIMUM_ORDER_AMOUNT = 10000;

  // Charger le panier depuis le stockage local au démarrage
  useEffect(() => {
    const savedCart = getStorageItem('cart');
    if (savedCart) {
      try {
        const { items, quantities } = JSON.parse(savedCart);
        if (Array.isArray(items)) setItems(items);
        if (quantities && typeof quantities === 'object') setQuantities(quantities);
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
      }
    }
  }, []);

  // Sauvegarder le panier dans le stockage local à chaque modification
  useEffect(() => {
    setStorageItem('cart', JSON.stringify({ items, quantities }));
  }, [items, quantities]);

  // Ajouter un produit au panier
  const addToCart = (product) => {
    // Si le produit n'est pas déjà dans le panier, l'ajouter
    if (!items.find(item => item.id === product.id)) {
      setItems([...items, product]);
      setQuantities({ ...quantities, [product.id]: 1 });
    } else {
      // Sinon, augmenter la quantité
      setQuantities({
        ...quantities,
        [product.id]: (quantities[product.id] || 0) + 1
      });
    }
  };

  // Retirer un produit du panier
  const removeFromCart = (productId) => {
    // Si la quantité est 1, retirer complètement le produit
    if (quantities[productId] === 1) {
      setItems(items.filter(item => item.id !== productId));
      const newQuantities = { ...quantities };
      delete newQuantities[productId];
      setQuantities(newQuantities);
    } else {
      // Sinon, diminuer la quantité
      setQuantities({
        ...quantities,
        [productId]: quantities[productId] - 1
      });
    }
  };

  // Retirer complètement un produit du panier
  const removeCompletelyFromCart = (productId) => {
    setItems(items.filter(item => item.id !== productId));
    const newQuantities = { ...quantities };
    delete newQuantities[productId];
    setQuantities(newQuantities);
  };

  // Mettre à jour la quantité d'un produit
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeCompletelyFromCart(productId);
    } else {
      setQuantities({
        ...quantities,
        [productId]: quantity
      });
    }
  };

  // Vider le panier
  const clearCart = () => {
    setItems([]);
    setQuantities({});
  };

  // Obtenir la quantité d'un produit
  const getItemQuantity = (productId) => {
    return quantities[productId] || 0;
  };

  // Calculer le total du panier
  const calculateTotal = () => {
    return items.reduce((total, item) => {
      return total + (item.price * quantities[item.id]);
    }, 0);
  };

  // Nombre total d'articles dans le panier
  const getTotalItems = () => {
    return Object.values(quantities).reduce((total, qty) => total + qty, 0);
  };

  // Calculer le montant restant pour atteindre le minimum
  const getRemainingAmount = () => {
    const total = calculateTotal();
    return total < MINIMUM_ORDER_AMOUNT ? MINIMUM_ORDER_AMOUNT - total : 0;
  };

  // Vérifier si le montant minimal est atteint
  const isMinimumReached = () => {
    return calculateTotal() >= MINIMUM_ORDER_AMOUNT;
  };

  return (
    <CartContext.Provider
      value={{
        items,
        quantities,
        currentlyDisplayed,
        currentView,
        setCurrentlyDisplayed,
        setCurrentView,
        addToCart,
        removeFromCart,
        removeCompletelyFromCart,
        updateQuantity,
        clearCart,
        getItemQuantity,
        calculateTotal,
        getTotalItems,
        MINIMUM_ORDER_AMOUNT,
        getRemainingAmount,
        isMinimumReached
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;